import * as React from "react";
import {useEffect} from "react";
import {CellTree, Tree, useTree} from "@table-library/react-table-library/tree";
import {Body, Cell, Header, HeaderCell, HeaderRow, Row, Table} from "@table-library/react-table-library/table";
import {formatCurrency, formatPercentBasedOnLocale} from "../../utils/numberUtil";
import {Link} from "react-router-dom";
import {PLAN_TREE_THEME} from "./portfolioUtil";
import {IAssetAllocationTreeNode, IPlanTreeNode} from "../../model/Portfolio.model";
import {ISelectValue} from "../common/Select.component";
import {useDispatch, useSelector} from "react-redux";
import {Action, IdState} from "@table-library/react-table-library/types/common";
import {setAssetAllocationSelectedProductAction, updateAssetAllocationExpandedRowsAction} from "./PortfolioActions";
import {scrollToElement} from "../../utils/browserUtil";
import {hasResearchAccess} from "../../../mainReducerMapSelectors";
import {assetAllocationExpandedRows, assetAllocationSelectedProduct} from "./PortfolioMapSelectors";
import {AonColors} from "../../../../css/Colors";
import {buildAssetAllocationTreeByDate} from "./portfolioPlanTreeUtil";
import {IApplicationRootState} from "../../../applicationState";
import {AnyThunkDispatch} from "../../../commonTypes";

export interface IAssetAllocationTreeTableProps {
    selectedDate: ISelectValue;
    datedPlanNodes: IPlanTreeNode[];
}
export const AssetAllocationTreeTable: React.FunctionComponent<IAssetAllocationTreeTableProps> = (props) => {
    const [nodes, setNodes] = React.useState<IAssetAllocationTreeNode[]>([]);
    const hasResearch = useSelector((state: IApplicationRootState) => hasResearchAccess(state));
    const expandedRowsFromStore = useSelector((state: IApplicationRootState) => assetAllocationExpandedRows(state));
    const selectedProduct = useSelector((state: IApplicationRootState) => assetAllocationSelectedProduct(state));
    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        if (selectedProduct) {
            setTimeout(() => {
                scrollToElement(`#link-${selectedProduct}`, 100, true);
            }, (10));
        }
    }, []);

    useEffect(() => {
        const date = (props.selectedDate.id as string);
        setNodes(buildAssetAllocationTreeByDate(props.datedPlanNodes, date));
    }, [props]);

    const data = {nodes};

    const onTreeChange = (action: Action, state: IdState) => {
        const expandedRowsToStore = state.ids.map((id) => id as number);
        dispatch(updateAssetAllocationExpandedRowsAction(expandedRowsToStore));
    };

    const tree: Tree<IAssetAllocationTreeNode> = useTree(data,
        {onChange: onTreeChange, state: {ids: expandedRowsFromStore}},
        {treeIcon: {
            iconRight: (
                <div className="far fa-chevron-right fa-chevron-tree"/>
            ),
            iconDown: (
                <div className="far fa-chevron-down  fa-chevron-tree"/>
            ),
        }},
    );


    const renderName = (item: IAssetAllocationTreeNode) => {
        return hasResearch && item.backstopId
            ? <Link to={`/products/${item.backstopId}`}
                    onClick={() => dispatch(setAssetAllocationSelectedProductAction(item.id))} id={`link-${item.id}`}>
                {item.name}
            </Link>
            : item.name;
    };

    const expandCollapse = () => {
        tree.fns.onToggleAll({isCarryForward: false, isPartialToAll: true});
    };

    const getCellStyle = (value: number|undefined) => {
        if (value === undefined) return {};
        return (value >= 1 || value <= -1) ? { color: AonColors.AonOrange } : {};
    };

    const formatPriorMarketValuePctOfTotal = (value: number|undefined) => {
        if (value === undefined) return "N/A";
        const formattedValue = formatPercentBasedOnLocale(value, 2);
        return value > 0 ? `+${formattedValue}` : formattedValue;
    };

    const renderTree = () => {
        return (
            <Table data={data}
                   tree={tree}
                   theme={PLAN_TREE_THEME("34% 15% 12% 12% 12% 15%")}
                   layout={{custom: true, fixedHeader: true}}>
                {(nodes: IAssetAllocationTreeNode[]) => (
                    <>
                        <Header>
                            <HeaderRow>
                                <HeaderCell>Category</HeaderCell>
                                <HeaderCell>Rating</HeaderCell>
                                <HeaderCell>% Total</HeaderCell>
                                <HeaderCell>% Total Change<br/>from Prior Period</HeaderCell>
                                <HeaderCell>% Category</HeaderCell>
                                <HeaderCell>Amount</HeaderCell>
                            </HeaderRow>
                        </Header>

                        <Body>
                            {nodes.map((item: IAssetAllocationTreeNode) => (
                                <Row key={item.id} item={item}
                                     className={item.nodes && item.nodes.length > 0 ? "portfolio__parent-node" : ""}>
                                    <CellTree item={item}>
                                        {renderName(item)}
                                    </CellTree>
                                    <Cell>{item.rating}</Cell>
                                    <Cell>{formatPercentBasedOnLocale(item.marketValuePctOfTotal, 2)}</Cell>
                                    <Cell style={getCellStyle(item.priorMarketValuePctOfTotal)}>
                                        {formatPriorMarketValuePctOfTotal(item.priorMarketValuePctOfTotal)}
                                    </Cell>
                                    <Cell>{formatPercentBasedOnLocale(item.marketValuePctOfParent, 2)}</Cell>
                                    <Cell>{formatCurrency(item.marketValue, 0)}</Cell>
                                </Row>
                            ))}
                        </Body>
                    </>
                )}
            </Table>
        );
    };

    return <div className="portfolio__tree-container" data-testid="portfolio__asset-allocation-tree">
        <div className="portfolio__header-container">
            <div className="portfolio__expand-collapse-container">
                <div className="portfolio__expand-collapse-text link-14 clickable" onClick={expandCollapse}>
                    Expand/Collapse All
                </div>
            </div>
        </div>
        <div className="portfolio__tree-table-container">
            {renderTree()}
        </div>
    </div>;
};